import React, {useState} from 'react';
import Form from "../../components/form/Form";
import DocumentRow from "../../components/DocumentRow";
import DocumentsSection from "../../components/DocumentsSection";
import Opportunity from "../../models/Opportunity";
import MultiFileUpload from "../../components/form/MultiFileUpload";
import {useAuth} from "../../providers/AuthProvider";
import {useOpportunity} from "../../providers/OpportunityProvider";

const TeamProposalUpload = ({onUpdate = () => true}) => {
    const {
        opportunity,
        isDisabled,
        changeProps,
        onRequest
    } = useOpportunity();

    const editable = opportunity.canEditProposal();

    const disabled = isDisabled || !editable;

    const mapProposal = (proposal = opportunity.latest_proposal) => {
        if (proposal && proposal instanceof File) {
            return {
                name: proposal.name,
                path: proposal
            }
        }
        return proposal
    }

    const [latest_proposal, setProposal] = useState(mapProposal());

    return (
        <div className='talent-documents-container'>

            <div className="proposal-document-header">
                <h3>Team Proposal</h3>
            </div>

            <Form
                background={"transparent"}
                onSubmit={console.warn}
                submitButton={false}
                isDisabled={disabled}
                model={{latest_proposal}}
                fields={[
                    {
                        style: styles.fieldSection,
                        fields: {
                            latest_proposal: (latest_proposal) ? {
                                component: (
                                    <div className="mb-5 mt-2 latest-proposal">
                                        <DocumentRow
                                            document={latest_proposal}
                                            onUpdate={file => setProposal(mapProposal(file))}
                                        />
                                    </div>
                                ),
                                columns: 12,
                            } : {
                                label: 'Team Proposal',
                                type: 'file',
                                required: false,
                                style: styles.field,
                                columns: 12
                            },
                        }
                    }
                ]}
                className={TB.mapClass("talent-opp-proposal-fields", {'no-edit': !opportunity.isProposing()})}>

                {opportunity.isProposing() && (
                    <div className="cover-letter-form-btns">
                        <button type="button" className="btn btn-primary mr-3"
                                onClick={() => {
                                    onRequest(() => {
                                        onUpdate({latest_proposal})
                                    })
                                }}
                                disabled={disabled || !(latest_proposal instanceof File)}>
                            Save
                        </button>
                        <button className="btn btn-outline-dark"
                                type="button" onClick={() => setProposal(mapProposal())}
                                disabled={disabled || !(latest_proposal instanceof File)}>
                            Cancel
                        </button>
                    </div>
                )}
            </Form>
        </div>
    )
}

const DocumentsEditTab = ({onUpdate = async () => true, getStepActions = false}) => {
    const {auth} = useAuth();
    const {
        opportunity,
        isDisabled,
        changeProps,
        onRequest
    } = useOpportunity();


    const editable = opportunity.canEditProposal();
    const deleteDocument = async (doc) => {
        let confirmDelete = await TB.sysAlert("talent.delete_proposal_slide_button_clicked");


        if (confirmDelete) {
            await onRequest(async () => {
                await opportunity.deleteDocument(doc);
                await TB.wait(100);
            })
        }
    }

    const renderProjectProposalSection = () => {
        let {
            latest_proposal
        } = opportunity;

        if (opportunity.isAdmin() && !latest_proposal) {
            return null
        }

        return (
            <div className='talent-project-proposal-container'>
                <div className="proposal-document-header">
                    <h3>
                        {opportunity.isForClientEngagementLead() ?
                            'Your Proposal' :
                            'Team Proposal'
                        }
                    </h3>

                    {!opportunity.isAdmin() && (
                        <p>
                            Uploaded by the {laravel.platform_name} Client Lead
                        </p>
                    )}
                </div>

                <DocumentRow
                    document={latest_proposal}
                    type="PDF"
                    emptyMessage={"No Proposal Yet"}
                />
            </div>
        );
    }

    const renderTeamMembersSlides = () => {

        let members = opportunity.teamMembers();
        if (!members) {
            return <div/>;
        }

        let user = auth.canManageTalent() ? opportunity.talentUser() : auth;
        members = TB.orderBy(members, member => member.id === user.id ? 1 : 0);

        return (
            <div className='talent-documents-container'>
                <div className="proposal-header-container">
                    <h2>
                        Team Member Uploaded Slides
                    </h2>
                </div>
                {members.length > 0 && members.map(member => {

                    if (member.id === user.id) {
                        return renderTalentDocumentsSection();
                    }
                    return (
                        <DocumentsSection
                            key={`member-${member.id}`}
                            header={member.firstNamePlusInitial()}
                            documents={member.talent_uploaded_proposals || []}
                            emptyMessage={'No Slides Yet'}
                            disabled={isDisabled}
                        />
                    )
                })}
            </div>
        );
    }

    const renderTalentDocumentsSection = () => {
        let documents = opportunity.talent_uploaded_proposals;
        if (!documents) {
            return <div/>;
        }

        return (
            <div className={opportunity.isProposingWithTeam() ? "talent-documents-container" : ""}>
                <DocumentsSection
                    className={opportunity.isProposingAsIndividual() ? "proposal-tab-header flex-row" : "proposal-document-header"}
                    header={!opportunity.isProposingAsIndividual() && 'My Slides'}
                    subheader={(
                        <span>
                            {opportunity.isProposingAsIndividual() ?
                                'Attach any relevant materials you want to share with the client regarding this project.' :
                                `Attach any additional materials you want to share. A ${laravel.platform_name} representative will advise you on other items required for the proposal.`
                            }
                            &nbsp;<small><b><i>(Optional)</i></b></small>
                        </span>
                    )}
                    documents={documents}
                    onDelete={opportunity.isProposing() ? (doc) => deleteDocument(doc) : undefined}
                    disabled={isDisabled}>

                    {opportunity.isProposing() && renderUploadForm()}

                </DocumentsSection>
            </div>
        );
    }

    const onFileUploaded = async (response, file) => {

        if (!TB.has(response, 'success.data.document')) {
            return;
        }

        return onRequest(async () => {
            let opp = {...opportunity};

            if (!opp.talent_uploaded_proposals) {
                opp.talent_uploaded_proposals = [];
            }
            opp.talent_uploaded_proposals.push({...response.success.data.document})

            file.remove();

            TB.event('Opportunities@updated', Opportunity.make({...opp}))
        })
    }

    const renderUploadForm = () => {
        let proposals = opportunity.talent_uploaded_proposals || [];
        let limit = 20 - proposals.length;

        if (limit <= 0) {
            return (
                <div className='empty-talent-project-documents-container text-danger text-italic mt-4'>
                    <i className="fa fa-exclamation-circle mr-1"/>Max number of documents reached. Delete existing
                    documents to be able to upload new ones.
                </div>
            )
        }

        return (
            <MultiFileUpload
                maxFiles={limit}
                uploadParams={{
                    url: `/api/talent-user-opportunities/${opportunity.id}/documents/create`,
                    fields: {
                        type: TB.invert(laravel.document_types)['Talent Uploaded Proposal']
                    }
                }}
                onFileUploaded={onFileUploaded}
            />
        )
    }


    return (
        <React.Fragment>
            {
                opportunity.isProposingWithTeam() && getStepActions && getStepActions(opportunity.isAdmin() ?
                    'Please upload the team proposal below so team members can view and provide input. '
                    : `A ${laravel.platform_name} representative or team admin will pull together the team proposal and upload when available. You can attach materials to include in the proposal below.`
                )
            }
            <div className="opp-proposal-documents-tab">
                {(opportunity.isProposingWithTeam() && opportunity.canAdmin()) &&
                    <TeamProposalUpload onUpdate={onUpdate}/>
                }

                {(opportunity.isProposingWithTeam() && opportunity.canAdmin()) ?
                    renderTeamMembersSlides() : renderTalentDocumentsSection()
                }
                {opportunity.isProposingWithTeam() && renderProjectProposalSection()}


            </div>
        </React.Fragment>
    );
};


export default DocumentsEditTab;
const styles = {
    fieldSectionCol: {
        flexDirection: 'column',
        alignItems: 'stretch'
    },
    fieldSection: {
        flexDirection: 'row',
        justifyContent: 'center'
    },
    field: {
        padding: '0 15px',
        marginTop: 0
    },
};